import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/ui/Sidebar";
import dayjs from "dayjs";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  LogOut,
  Play,
  Pause,
  MapPin,
  Menu,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import MobileNBottomNav from "../components/ui/MobileBottomNav";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AddEventFormFields from "./AddEventFormFields";
import SubscribeModal from "./SubscriptionModal";
import { API_ENDPOINTS } from "../lib/config";
import { fetchWithAuth } from "../lib/authService";

const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Format date for badges
const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const Calendar = (Props) => {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState(dayjs().toISOString());
  const [events, setEvents] = useState({});
  const [allEvents, setAllEvents] = useState([]);

  const startOfMonth = currentDate.startOf("month");
  const startDay = startOfMonth.day();
  const daysInMonth = currentDate.daysInMonth();

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetchWithAuth(API_ENDPOINTS.EVENT_PERFORMER, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) {
          toast.error("Failed to fetch session data");
          throw new Error("Failed to fetch session data");
        }
        const data = await response.json();
        setAllEvents(data || []);
        // Filter for selected date
        const selectdate = dayjs(selectedDate).format("YYYY-MM-DD");
        const currentevents = (data || []).filter(
          (event) => dayjs(event.startAt).format("YYYY-MM-DD") === selectdate,
        );
        setEvents(currentevents);
        Props.setNowEvents && Props.setNowEvents(currentevents);
      } catch (error) {
        setAllEvents([]);
        setEvents([]);
        console.error("Error fetching events:", error);
        toast.error("Error fetching events!");
      }
    };
    fetchEvents();
  }, [selectedDate, Props.setNowEvents]);

  const prevMonth = () => setCurrentDate(currentDate.subtract(1, "month"));
  const nextMonth = () => setCurrentDate(currentDate.add(1, "month"));

  const eventDates = useMemo(() => {
    if (!allEvents) return new Set();
    return new Set(
      allEvents.map((ev) => dayjs(ev.startAt).format("YYYY-MM-DD")),
    );
  }, [allEvents]);

  const handleDayClick = (day) => {
    const fullDate = currentDate.date(day);
    setSelectedDate(fullDate.toISOString());
  };

  const renderDays = () => {
    const days = [];
    for (let i = 0; i < startDay; i++) {
      days.push(
        <div
          key={`empty-${i}`}
          className="h-12 md:h-16 w-full aspect-square"
        />,
      );
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const thisDay = currentDate.date(i);
      const isSelected =
        selectedDate && dayjs(selectedDate).isSame(thisDay, "day");
      const dateStr = thisDay.format("YYYY-MM-DD");
      const hasEvent = eventDates.has(dateStr);

      days.push(
        <div
          key={i}
          onClick={() => handleDayClick(i)}
          className={`h-12 md:h-16 w-full aspect-square p-1 md:p-2 rounded-lg border-2 cursor-pointer flex flex-col items-center justify-center ${
            isSelected
              ? "border-logo bg-logo/10"
              : "border-transparent hover:border-logo/30 hover:bg-logo/5"
          }`}
        >
          <span
            className={`text-xs md:text-sm font-medium ${
              isSelected ? "text-logo" : "text-[#2D0A4A]"
            }`}
          >
            {i}
          </span>
          {hasEvent && (
            <span className="mt-1 w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-logo animate-pulse" />
          )}
        </div>,
      );
    }
    return days;
  };

  return (
    <div className="bg-gray-100 p-2 md:p-4 w-full mx-auto">
      {/* Header */}
      <div className="bg-gray-100/80 backdrop-blur-sm rounded-2xl p-4 md:p-6 mb-4 md:mb-6 border border-purple-100">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <span className="h-8 w-8 flex items-center justify-center text-logo text-2xl font-bold">
              📅
            </span>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-[#2D0A4A]">
                Event Calendar
              </h1>
              <p className="text-sm md:text-base text-[#4A4A4A]">
                Manage your performance schedule
              </p>
            </div>
          </div>
          <button
            className="flex items-center space-x-2 bg-gradient-to-r from-logo to-[#6720A3] text-white px-4 py-2 md:px-6 md:py-3 rounded-xl font-medium shadow-lg w-full md:w-auto justify-center"
            onClick={() =>
              Props.profile?.isVerified
                ? Props.setAddEvent(true)
                : Props.setIsModalOpen(true)
            }
          >
            <Plus className="h-4 w-4 md:h-5 md:w-5" />
            <span>Add Event</span>
          </button>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-gray-100/80 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-purple-100">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <button
            onClick={prevMonth}
            className="p-1 md:p-2 rounded-lg hover:bg-logo/10 transition-colors"
          >
            <ChevronLeft className="h-5 w-5 md:h-6 md:w-6 text-logo" />
          </button>
          <h2 className="text-lg md:text-xl font-bold text-[#2D0A4A]">
            {currentDate.format("MMMM YYYY")}
          </h2>
          <button
            onClick={nextMonth}
            className="p-1 md:p-2 rounded-lg hover:bg-logo/10 transition-colors"
          >
            <ChevronRight className="h-5 w-5 md:h-6 md:w-6 text-logo" />
          </button>
        </div>

        {/* Days of Week */}
        <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2 md:mb-4">
          {daysOfWeek.map((day) => (
            <div
              key={day}
              className="text-center text-xs md:text-sm font-medium text-[#4A4A4A] py-1 md:py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 md:gap-2">{renderDays()}</div>
      </div>
    </div>
  );
};

export default function ArtistCalender() {
  const navigate = useNavigate();
  const [sidebarPage, setSidebarPage] = useState("calendar");
  const [profile, setProfile] = useState({});
  const [nowevents, setNowEvents] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const scrollRef = useRef(null);
  const [liveId, setLiveId] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [showPauseConfirm, setShowPauseConfirm] = useState(false);
  const [showResumeConfirm, setShowResumeConfirm] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showLiveConfirm, setShowLiveConfirm] = useState(false);
  const [showEndLiveConfirm, setShowEndLiveConfirm] = useState(false);
  const [selectedEventForLive, setSelectedEventForLive] = useState(null);
  const [addEvent, setAddEvent] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (nowevents.length > 0 && scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [nowevents]);

  useEffect(() => {
    const token = localStorage.getItem("tringAuthToken");
    if (!token) {
      navigate("/");
      return;
    }
    const fetchProfile = async () => {
      try {
        const profRes = await fetchWithAuth(API_ENDPOINTS.PARTNER);
        const prof = await profRes.json();
        setProfile(prof);
        
        // Fetch subscription details: try GET first, if 404 then create via POST
        let subData = null;
        try {
          const subGet = await fetchWithAuth(API_ENDPOINTS.SUBSCRIPTION, {
            method: "GET",
          });
          if (subGet.ok) {
            subData = await subGet.json();
          } else if (subGet.status === 404) {
            const subPost = await fetchWithAuth(API_ENDPOINTS.SUBSCRIPTION, {
              method: "POST",
            });
            if (!subPost.ok) throw new Error("Failed to create subscription");
            subData = await subPost.json();
          } else {
            throw new Error(`Subscription fetch failed: ${subGet.status}`);
          }
        } catch (e) {
          console.error("Subscription fetch/create failed:", e);
        }
        setSubscription(subData);
      } catch (err) {
        setProfile({});
        toast.error("Failed to load profile");
      }
    };
    fetchProfile();
  }, []);

  const handleLiveToggle = (venueId) => {
    const event = nowevents.find((v) => v.venueId === venueId);
    const activeEvent = nowevents.find(
      (v) => v.status === "active" || v.status === "paused",
    );
    if (event.status === "active") {
      setSelectedEventForLive(venueId);
      setShowEndLiveConfirm(true);
    } else if (
      (event.status === "approved" || event.status === "paused") &&
      (!activeEvent || activeEvent.venueId === venueId)
    ) {
      setSelectedEventForLive(venueId);
      setShowLiveConfirm(true);
    } else {
      toast.error(
        "Please finish the current live event before going live again",
      );
    }
  };

  const startLiveSession = async (venueId) => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.VENUE_STATUS(venueId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: "active" }),
      });

      // Check if response is OK
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to go live");
      }

      setLiveId(venueId);
      toast.success("Event is live now");
      navigate(`/EventDetails/${venueId}`);
    } catch (err) {
      toast.error("Failed to go live. Please try again.");
    }
  };

  const endLiveSession = async (venueId) => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.VENUE_STATUS(venueId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: "finished" }),
      });

      // Check if response is OK
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to end session");
      }

      setLiveId(null);
      toast.success("Event is finished now");
      // Refresh events for the selected date
    } catch (err) {
      console.error("Error ending session:", err);
      toast.error(err?.message || "Failed to end session. Please try again.");
    }
  };

  const handlePause = (venueId) => {
    setSelectedEventId(venueId);
    setShowPauseConfirm(true);
  };
  const pauseEvent = async (venueId) => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.VENUE_STATUS(venueId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: "paused" }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to pause session");
      }
      toast.success("Session paused");
      // Refresh events for the selected date
    } catch (err) {
      toast.error("Error pausing event. Please try again later.");
    }
  };

  const handleResume = (venueId) => {
    setSelectedEventId(venueId);
    setShowResumeConfirm(true);
  };
  const resumeEvent = async (venueId) => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.VENUE_STATUS(venueId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: "active" }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to resume session");
      }
      toast.success("Session resumed");
      // Refresh events for the selected date
    } catch (err) {
      toast.error("Error resuming event. Please try again later.");
    }
  };

  const handleEnd = (id) => {
    const event = nowevents.find((v) => v.id === id);
    if (event?.status !== "active") {
      toast.error("You can only end a live session!");
      return;
    }
    setSelectedEventId(id);
    setShowEndConfirm(true);
  };

  // --- Banner/Badge Styling and Button Layout (as in LoggedInArtist) ---
  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return "bg-green-500";
      case "finished":
        return "bg-red-500";
      case "paused":
        return "bg-yellow-500";
      case "inactive":
      default:
        return "bg-logo";
    }
  };
  const getStatusText = (status) => {
    switch (status) {
      case "active":
        return "Live";
      case "finished":
        return "Ended";
      case "paused":
        return "Paused";
      case "inactive":
      default:
        return "Upcoming";
    }
  };

  // Event click
  const handleEventClick = (event) => {
    if (event.status === "active") {
      navigate(`/EventDetails/${event.venueId}`);
    } else if (event.status === "finished") {
      navigate(`/venue/${event.id}/overview`);
    } else {
      toast.error("Make event live to enter the event");
    }
  };

  const handleSidebarNav = (page) => {
    setSidebarPage(page);
    setIsSidebarOpen(false);
    switch (page) {
      case "home":
        navigate("/loggedinartist");
        break;
      case "calendar":
        navigate("/bookevent");
        break;
      case "qr":
        navigate("/me");
        break;
      case "profile":
        navigate("/artistprofile");
        break;
      default:
        break;
    }
  };

  const handleAddEventSuccess = () => {
    setAddEvent(false);
    const token = localStorage.getItem("tringAuthToken");
    fetchWithAuth(API_ENDPOINTS.OWNER_VENUES)
      .then((res) => res.json())
      .then((evData) => setNowEvents(evData.venues || []));
    toast.success("Event added successfully!");
  };

  const displayed = useMemo(() => {
    return nowevents.sort((a, b) => {
      const getStatusOrder = (status) => {
        if (status === "active" || status === "paused") return 0;
        if (status === "inactive") return 1;
        return 2;
      };
      const statusA = getStatusOrder(a.status);
      const statusB = getStatusOrder(b.status);
      if (statusA !== statusB) return statusA - statusB;
      if (a.status === "inactive" && b.status === "inactive") {
        return new Date(a.startAt) - new Date(b.startAt);
      }
      return 0;
    });
  }, [nowevents]);

  return (
    <div className="flex min-h-screen w-screen bg-gradient-to-br from-white via-[#F8F4FB] to-[#F0EBF5] relative md:mt-0 mt-20">
      <ToastContainer position="top-center" autoClose={3000} />
      <MobileNBottomNav />
      {/* Mobile Sidebar Toggle */}
      <div className="fixed top-0 left-0 z-50 w-full px-2 py-3 flex items-center justify-between bg-logo backdrop-blur-sm lg:hidden">
        <div className="flex items-center gap-2">
          <img src="/images/tringbox-logo.png" className="h-6" />
        </div>
        <motion.button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="p-2 rounded-md"
          aria-label="Toggle sidebar"
        >
          {isSidebarOpen ? (
            <X size={24} className="text-white" />
          ) : (
            <Menu size={24} className="text-white" />
          )}
        </motion.button>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-0 w-80 h-full transition-transform duration-300 ease-in-out z-40 ${
          isSidebarOpen ? "translate-x-0" : "lg:translate-x-0 -translate-x-full"
        }`}
      >
        <Sidebar
          currentPage={sidebarPage}
          onPageChange={handleSidebarNav}
          profile={profile}
        />
      </div>

      {/* Main Content */}
      <main
        ref={scrollRef}
        className="flex-1 min-h-screen lg:pl-80 lg:py-0 z flex flex-col w-full overflow-y-auto"
      >
        <div>
          <Calendar
            setNowEvents={setNowEvents}
            navigate={navigate}
            setAddEvent={setAddEvent}
            profile={profile}
            setIsModalOpen={setIsModalOpen}
          />
        </div>
        <div className="md:p-4 p-2 space-y-2 mb-20">
          {displayed.map((event) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="w-full border border-neutral-600 relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow mb-4"
            >
              {/* Date Badge */}
              <div className="absolute top-2 left-2 z-10">
                <div className="px-3 py-1 rounded-full text-xs font-medium bg-black/60 text-white shadow-lg backdrop-blur-sm bg-opacity-90">
                  {formatDate(event.startAt)}
                </div>
              </div>
              {/* Status Badge */}
              <div
                className={`absolute top-2 right-2 z-10 px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(
                  event.status,
                )} text-white shadow-lg bg-opacity-90`}
              >
                {getStatusText(event.status)}
              </div>
              {/* Event Card Layout */}
              <div className="flex flex-col sm:flex-row w-full h-full">
                {/* Event Image */}
                <div
                  className="relative w-full sm:w-1/3 h-full overflow-hidden cursor-pointer"
                  onClick={() => handleEventClick(event)}
                >
                  <img
                    src={
                      event.photoUrl || "https://via.placeholder.com/400x200"
                    }
                    alt={event.name}
                    className="w-full lg:h-40 sm:h-40 h-40 object-cover"
                  />
                </div>
                {/* Details and Buttons */}
                <div className="sm:w-2/3 p-3 lg:p-4 flex flex-col justify-between relative">
                  <div>
                    <div
                      className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2"
                      onClick={() => handleEventClick(event)}
                    >
                      <h3 className="text-lg lg:text-xl font-bold text-gray-900 cursor-pointer">
                        {event.name}
                      </h3>
                    </div>
                    <div className="flex items-center text-xs lg:text-sm text-gray-500">
                      <MapPin className="h-3 w-3 lg:h-4 lg:w-4 mr-1 flex-shrink-0" />
                      <span className="truncate">
                        {event.venue?.address?.line1
                          ? `${event.venue.address.line1}${
                              event.venue.address.city
                                ? ", " + event.venue.address.city
                                : ""
                            }${
                              event.venue.address.state
                                ? ", " + event.venue.address.state
                                : ""
                            }`
                          : "No address"}
                      </span>
                    </div>
                  </div>
                  {/* Buttons */}
                  <div className="flex flex-wrap w-full items-center gap-2 lg:gap-3 md:mt-4 mt-3 lg:mt-0 lg:right-4">
                    {(event.status === "active" ||
                      event.status === "approved" ||
                      event.status === "paused") && (
                      <label className="inline-flex items-center cursor-pointer ">
                        <input
                          type="checkbox"
                          checked={
                            event.status === "active" ||
                            event.status === "paused"
                          }
                          onChange={() => handleLiveToggle(event.venueId)}
                          className="sr-only peer"
                        />
                        <div
                          className={`
                            w-10 h-5 lg:w-12 lg:h-6 rounded-full relative
                            after:content-[''] after:absolute after:top-0.5 after:left-[2px]
                            after:bg-white after:border after:border-gray-300 after:rounded-full after:h-4 after:w-4 lg:after:h-5 lg:after:w-5 after:transition-all
                            peer-checked:after:translate-x-5 lg:peer-checked:after:translate-x-6
                            peer-checked:bg-green-600 bg-gray-300
                          `}
                        />
                        <span
                          className={`ml-2 text-xs lg:text-sm font-semibold ${
                            event.status === "active"
                              ? "text-green-600"
                              : "text-gray-500"
                          }`}
                        >
                          Live
                        </span>
                      </label>
                    )}
                    <div className="flex-1" />
                    {event.status === "active" && (
                      <>
                        <button
                          onClick={() => handlePause(event.venueId)}
                          className="px-3 lg:px-4 py-2 border-2 border-logo text-logo rounded-lg font-medium flex items-center space-x-1 lg:space-x-2 hover:bg-logo hover:text-white transition-colors text-xs lg:text-sm"
                        >
                          <Pause className="h-3 w-3 lg:h-4 lg:w-4" />
                          <span>Pause</span>
                        </button>
                        <button
                          onClick={() => handleEnd(event.venueId)}
                          className="px-3 lg:px-4 py-2 bg-red-500 text-white rounded-lg font-medium flex items-center space-x-1 lg:space-x-2 hover:bg-red-600 transition-colors text-xs lg:text-sm"
                        >
                          <LogOut className="h-3 w-3 lg:h-4 lg:w-4" />
                          <span>End</span>
                        </button>
                      </>
                    )}
                    {event.status === "paused" && (
                      <>
                        <button
                          onClick={() => handleResume(event.venueId)}
                          className="px-3 lg:px-4 py-2 bg-logo text-white rounded-lg font-medium flex items-center space-x-1 lg:space-x-2 hover:bg-[#62187D] transition-colors text-xs lg:text-sm"
                        >
                          <Play className="h-3 w-3 lg:h-4 lg:w-4" />
                          <span>Resume</span>
                        </button>
                        <button
                          onClick={() => handleEnd(event.venueId)}
                          className="px-3 lg:px-4 py-2 bg-red-500 text-white rounded-lg font-medium flex items-center space-x-1 lg:space-x-2 hover:bg-red-600 transition-colors text-xs lg:text-sm"
                        >
                          <LogOut className="h-3 w-3 lg:h-4 lg:w-4" />
                          <span>End</span>
                        </button>
                      </>
                    )}
                    {event.status === "approved" && (
                      <>
                        <button
                          className="px-3 lg:px-4 py-2 border-2 border-gray-400 text-gray-400 rounded-lg font-medium flex items-center space-x-1 lg:space-x-2 transition-colors text-xs lg:text-sm"
                          disabled
                        >
                          <Pause className="h-3 w-3 lg:h-4 lg:w-4" />
                          <span>Pause</span>
                        </button>
                        <button
                          className="px-3 lg:px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center space-x-1 lg:space-x-2 text-xs lg:text-sm"
                          disabled
                        >
                          <LogOut className="h-3 w-3 lg:h-4 lg:w-4" />
                          <span>End</span>
                        </button>
                      </>
                    )}
                    {event.status === "finished" && (
                      <button
                        onClick={() => navigate(`/venue/${event.id}/overview`)}
                        className="px-3 lg:px-4 py-2 bg-logo text-white md:w-max w-full rounded-lg font-medium hover:bg-[#62187D] transition-colors text-xs lg:text-sm"
                      >
                        Summary
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        {/* Modals Section: Pause/Resume/End/GoLive/EndLive */}
        {/* ... (keep your modal code as you have, it's compatible) ... */}

        {showPauseConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Pause className="w-8 h-8 text-yellow-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Pause Session
                </h3>
                <p className="text-gray-600 mb-2">
                  Are you sure you want to pause this session?
                </p>
                <div className="bg-yellow-50 rounded-lg p-4 text-sm text-yellow-800">
                  <ul className="list-disc  space-y-1 text-start">
                    <li>Users won't be able to make new requests</li>
                    <li>You can resume the session at any time</li>
                    <li>Current requests will be preserved</li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium"
                  onClick={() => setShowPauseConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 py-3 px-4 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl font-medium"
                  onClick={() => {
                    pauseEvent(selectedEventId);
                    setShowPauseConfirm(false);
                  }}
                >
                  Pause Session
                </button>
              </div>
            </div>
          </div>
        )}

        {showResumeConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Play className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Resume Session
                </h3>
                <p className="text-gray-600 mb-2">
                  Are you sure you want to resume this session?
                </p>
                <div className="bg-green-50 rounded-lg p-4 text-sm text-green-800">
                  <ul className="list-disc text-start space-y-1">
                    <li>Users can start making requests again</li>
                    <li>Previous requests will be available</li>
                    <li>You can pause again if needed</li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium"
                  onClick={() => setShowResumeConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 py-3 px-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium"
                  onClick={() => {
                    resumeEvent(selectedEventId);
                    setShowResumeConfirm(false);
                  }}
                >
                  Resume Session
                </button>
              </div>
            </div>
          </div>
        )}

        {showEndConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <LogOut className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  End Session
                </h3>
                <p className="text-gray-600 mb-2">
                  Are you sure you want to end this session?
                </p>
                <div className="bg-red-50 rounded-lg p-4 text-sm text-red-800">
                  <ul className="list-disc text-start space-y-1">
                    <li>All current requests will be lost</li>
                    <li>Session cannot be resumed once ended</li>
                    <li>This action cannot be undone</li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium"
                  onClick={() => setShowEndConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 py-3 px-4 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium"
                  onClick={() => {
                    endLiveSession(selectedEventId);
                    setShowEndConfirm(false);
                  }}
                >
                  End Session
                </button>
              </div>
            </div>
          </div>
        )}

        {showLiveConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Play className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Start Live Session
                </h3>
                <p className="text-gray-600 mb-2">
                  Are you sure you want to start this live session?
                </p>
                <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800">
                  <ul className="list-disc list-inside space-y-1">
                    <li>Users will be able to make song requests</li>
                    <li>You can manage requests and playlists</li>
                    <li>Session can be paused or extended if needed</li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium"
                  onClick={() => setShowLiveConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 py-3 px-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                  onClick={() => {
                    startLiveSession(selectedEventForLive);
                    setShowLiveConfirm(false);
                  }}
                >
                  <Play className="w-4 h-4" />
                  Start Live
                </button>
              </div>
            </div>
          </div>
        )}

        {showEndLiveConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <LogOut className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  End Live Session
                </h3>
                <p className="text-gray-600 mb-2">
                  Are you sure you want to end this live session?
                </p>
                <div className="bg-red-50 rounded-lg p-4 text-sm text-red-800">
                  <ul className="list-disc list-inside space-y-1">
                    <li>All current requests will be lost</li>
                    <li>Users won't be able to make new requests</li>
                    <li>Session cannot be resumed once ended</li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium"
                  onClick={() => setShowEndLiveConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 py-3 px-4 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                  onClick={() => {
                    endLiveSession(selectedEventForLive);
                    setShowEndLiveConfirm(false);
                  }}
                >
                  <LogOut className="w-4 h-4" />
                  End Session
                </button>
              </div>
            </div>
          </div>
        )}

        {addEvent && (
          <AddEventFormFields
            onSuccess={handleAddEventSuccess}
            setAddEvent={setAddEvent}
          />
        )}

        <SubscribeModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          subscriptionData={subscription}
        />
      </main>
    </div>
  );
}
